import React, { useState, useEffect } from "react";
import styles from "../../../styles/MelodyQuiz.module.css";
import { ScaleType, getScaleTypeLabel } from "../../../utils/musicTheory";
import { MelodyQuizOptions } from "./MelodyQuiz";
import ModeSelector, { QuizMode } from "../../../components/ModeSelector";
import Tooltip from "../../../components/Tooltip";
import { getScaleDegreeReference } from "../../../data/scaleReferences";
import { Note } from "../../../soundLibrary";

interface MelodyPreQuizProps {
    setQuizOptions: (options: MelodyQuizOptions) => void;
    initialOptions?: MelodyQuizOptions;
}

const allScaleTypes: ScaleType[] = ["MAJOR", "NATURAL_MINOR", "HARMONIC_MINOR"];
const MELODY_LENGTHS = [4, 6, 8];
const SCALE_DEGREES = [1, 2, 3, 4, 5, 6, 7];
const availableKeys: Note[] = ["C4" as Note, "G4" as Note, "D4" as Note, "A4" as Note, "E4" as Note];

export default function MelodyPreQuiz({ setQuizOptions, initialOptions }: MelodyPreQuizProps) {
    const [selectedScale, setSelectedScale] = useState<ScaleType>(
        initialOptions?.selectedScales && initialOptions.selectedScales.length > 0
            ? initialOptions.selectedScales[0]
            : "MAJOR"
    );
    const [selectedDegrees, setSelectedDegrees] = useState<number[]>(
        initialOptions?.selectedDegrees ?? [1, 2, 3, 5]
    );
    const [melodyLength, setMelodyLength] = useState<number>(initialOptions?.melodyLength ?? 6);
    const [mode, setMode] = useState<QuizMode>(initialOptions?.mode ?? "questions");
    const [duration, setDuration] = useState<number>(initialOptions?.timeLimitSeconds ?? 60);
    const [questionLimit, setQuestionLimit] = useState<number>(initialOptions?.questionLimit ?? 10);
    const [difficulty, setDifficulty] = useState<number>(initialOptions?.difficulty ?? 1);
    const [referenceNote, setReferenceNote] = useState<Note>(initialOptions?.referenceNote ?? ("C4" as Note));

    const toggleDegree = (degree: number) => {
        setSelectedDegrees((prev) =>
            prev.includes(degree)
                ? prev.filter((d) => d !== degree)
                : [...prev, degree].sort((a, b) => a - b)
        );
    };

    const toggleAllDegrees = () => {
        if (selectedDegrees.length === SCALE_DEGREES.length) {
            setSelectedDegrees([]);
        } else {
            setSelectedDegrees([...SCALE_DEGREES]);
        }
    };

    useEffect(() => {
        setQuizOptions({
            selectedScales: [selectedScale],
            selectedDegrees,
            melodyLength,
            mode,
            timeLimitSeconds: mode === "time" ? duration : undefined,
            questionLimit: mode === "questions" ? questionLimit : undefined,
            difficulty,
            referenceNote,
        });
    }, [selectedScale, selectedDegrees, melodyLength, mode, duration, questionLimit, difficulty, referenceNote, setQuizOptions]);

    return (
        <div className={styles.preQuizWrapper}>
            <ModeSelector value={mode} onChange={setMode} />

            {mode === "time" && (
                <div className={styles.drillOptionLabel}>
                    <label className={styles.comboLabel}>
                        Duration:{" "}
                        <input
                            type="number"
                            value={duration}
                            min={10}
                            max={600}
                            onChange={(e) => {
                                setDuration(Number(e.target.value));
                            }}
                            className={styles.preQuizCounter}
                        />
                    </label>
                </div>
            )}

            {mode === "questions" && (
                <div className={styles.drillOptionLabel}>
                    <label className={styles.comboLabel}>
                        Question limit:{" "}
                        <input
                            type="number"
                            value={questionLimit}
                            min={1}
                            max={100}
                            onChange={(e) => {
                                setQuestionLimit(Number(e.target.value));
                            }}
                            className={styles.preQuizCounter}
                        />
                    </label>
                </div>
            )}

            <div className={styles.drillOptionLabel}>
                <label className={styles.comboLabel}>
                    Melody Length:{" "}
                    <select
                        value={melodyLength}
                        onChange={(e) => setMelodyLength(Number(e.target.value))}
                        className={styles.preQuizCombo}
                    >
                        {MELODY_LENGTHS.map((length) => (
                            <option key={length} value={length}>
                                {length} notes
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            <div className={styles.drillOptionLabel}>
                <label className={styles.comboLabel}>
                    Scale Type:{" "}
                    <select
                        value={selectedScale}
                        onChange={(e) => setSelectedScale(e.target.value as ScaleType)}
                        className={styles.preQuizCombo}
                    >
                        {allScaleTypes.map((scaleType) => (
                            <option key={scaleType} value={scaleType}>
                                {getScaleTypeLabel(scaleType)}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            <div className={styles.drillOptionLabel}>
                <label className={styles.comboLabel}>
                    Reference Note:{" "}
                    <select
                        value={referenceNote}
                        onChange={(e) => setReferenceNote(e.target.value as Note)}
                        className={styles.preQuizCombo}
                    >
                        {availableKeys.map((key) => (
                            <option key={key} value={key}>
                                {key}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            <div className={styles.drillOptionLabel}>
                <label className={styles.comboLabel}>
                    <Tooltip
                        content="Difficulty determines how similar the answer choices are. Easy has very different answers, while Hard has very similar answers."
                        position="top"
                    >
                        <span style={{ cursor: "help" }}>Difficulty:</span>
                    </Tooltip>
                    {" "}
                    <select
                        value={difficulty}
                        onChange={(e) => setDifficulty(Number(e.target.value))}
                        className={styles.preQuizCombo}
                    >
                        <option value={1}>Easy</option>
                        <option value={2}>Medium</option>
                        <option value={3}>Hard</option>
                    </select>
                </label>
            </div>

            <div className={styles.selectAllWrapper} style={{ marginTop: "1rem" }}>
                <div>Scale Degrees:</div>
                <button onClick={toggleAllDegrees} className={styles.preQuizButton}>
                    {selectedDegrees.length === SCALE_DEGREES.length ? "Deselect All" : "Select All"}
                </button>
            </div>

            <div className={styles.degreesWrapper}>
                {SCALE_DEGREES.map((degree) => {
                    const ref = getScaleDegreeReference(degree);
                    const tooltipContent = ref ? (
                        <div className={styles.scaleDegreeHint}>
                            <div className={styles.scaleDegreeHintSolfege}>{ref.solfege}</div>
                            <div className={styles.scaleDegreeHintName}>{ref.name}</div>
                            <div className={styles.scaleDegreeHintCharacter}>{ref.character}</div>
                        </div>
                    ) : null;

                    return (
                        <Tooltip key={degree} content={tooltipContent} position="top">
                            <button
                                onClick={() => toggleDegree(degree)}
                                className={
                                    selectedDegrees.includes(degree)
                                        ? `${styles.degreeButton} ${styles.degreeButtonActive}`
                                        : styles.degreeButton
                                }
                            >
                                {degree}
                            </button>
                        </Tooltip>
                    );
                })}
            </div>
        </div>
    );
}
